/**
 * BlockWrapper component provides consistent spacing for landing page blocks.
 * It wraps block content with standardized padding and internal spacing based on
 * the global spacing configuration from SiteConfig.
 *
 * This component:
 * - Applies consistent max-width and centering
 * - Uses spacing from global configuration
 * - Does NOT handle backgrounds (preserves SectionBackgroundRenderer)
 * - Supports custom variants for special cases
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useSpacingConfig, useIsGlobalSpacingEnabled } from "./spacingUtils";

export interface BlockWrapperProps {
  /** Content to be wrapped */
  children: React.ReactNode;

  /** Optional id for anchoring */
  id?: string;

  /** Optional custom max-width. Defaults to '7xl' (1280px) */
  maxWidth?: "full" | "5xl" | "6xl" | "7xl";

  /** Apply internal vertical spacing between child elements */
  applyInternalSpacing?: boolean;

  /** Apply horizontal padding to block content */
  applyHorizontalPadding?: boolean;

  /** Apply vertical padding to block content */
  applyVerticalPadding?: boolean;

  /** Additional custom className to merge with spacing classes */
  className?: string;

  /** Override default spacing behavior (for special blocks that need custom spacing) */
  disableGlobalSpacing?: boolean;

  /** ARIA role for accessibility */
  role?: string;

  /** ARIA label for accessibility */
  "aria-label"?: string;
}

const MAX_WIDTH_CLASSES: Record<NonNullable<BlockWrapperProps["maxWidth"]>, string> = {
  full: "max-w-full",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
};

/**
 * BlockWrapper component that applies consistent spacing to landing page blocks.
 */
export const BlockWrapper: React.FC<BlockWrapperProps> = ({
  children,
  id,
  maxWidth = "7xl",
  applyInternalSpacing = true,
  applyHorizontalPadding = true,
  applyVerticalPadding = true,
  className,
  disableGlobalSpacing = false,
  role,
  "aria-label": ariaLabel,
}) => {
  const spacingClasses = useSpacingConfig();
  const isGlobalSpacingEnabled = useIsGlobalSpacingEnabled();

  // Determine if we should apply global spacing
  const shouldApplySpacing =
    !disableGlobalSpacing && isGlobalSpacingEnabled;

  // Build the class string
  const classes = cn(
    // Always apply max-width and centering
    MAX_WIDTH_CLASSES[maxWidth],
    "mx-auto",

    // Apply flex column to make space-y work properly
    "flex flex-col",

    // Apply horizontal padding if enabled
    shouldApplySpacing &&
      applyHorizontalPadding &&
      spacingClasses.block.paddingX,

    // Apply vertical padding if enabled
    shouldApplySpacing &&
      applyVerticalPadding &&
      spacingClasses.block.paddingY,

    // Apply internal spacing if enabled
    shouldApplySpacing &&
      applyInternalSpacing &&
      spacingClasses.block.internal,

    // Merge any custom classes
    className
  );

  return (
    <div id={id} className={classes} role={role} aria-label={ariaLabel}>
      {children}
    </div>
  );
};

/**
 * Get section wrapper classes from spacing configuration.
 * Use this in section index files to apply consistent section padding.
 */
export function useSectionWrapperClasses(): string {
  const spacingClasses = useSpacingConfig();
  const isGlobalSpacingEnabled = useIsGlobalSpacingEnabled();

  if (!isGlobalSpacingEnabled) {
    // Fallback to default if global spacing is disabled
    return "py-12 sm:py-16 lg:py-20 xl:py-24";
  }

  return spacingClasses.section.vertical;
}

/**
 * Get section horizontal padding classes from spacing configuration.
 * Use this in section container divs.
 */
export function useSectionHorizontalPadding(): string {
  const spacingClasses = useSpacingConfig();
  const isGlobalSpacingEnabled = useIsGlobalSpacingEnabled();

  if (!isGlobalSpacingEnabled) {
    // Fallback to default if global spacing is disabled
    return "container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12";
  }

  return cn("container mx-auto", spacingClasses.section.horizontal);
}
